import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {TITLE_SUFFIX} from '../../config/constants';
import {GSSKT_HOST} from '../../config/constants/hosts';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {ColumnListTopPage} from '../../features/columns/components/List/TopPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

export type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {},
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    return {
      props: {
        userDataForAnalytics: userDataResponse.ok
          ? userDataResponse.value
          : undefined,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({userDataForAnalytics}: Props) => {
  const router = useRouter();
  const canonicalURL = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const title = `コラム${TITLE_SUFFIX}`;
  const description =
    '外資系金融・コンサル・商社・外資メーカーなど、トップ企業志望者必見のコラムを幅広く掲載。現役社員・内定者の体験談、実践的な選考ノウハウを通し、志望企業の内定獲得につなげよう！';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key="description" name="description" content={description} />
        <meta
          key="keywords"
          name="keywords"
          content="コラムまとめ,コラム,就活の情報収集,外資,就活,インターンシップ,新卒採用,会社説明会"
        />
        <link rel="canonical" href={canonicalURL} key="canonical" />
        <meta
          key="twitter:title"
          name="twitter:title"
          content="コラムまとめ | 外資就活ドットコム"
        />
        <meta
          key="og:title"
          property="og:title"
          content="コラムまとめ | 外資就活ドットコム"
        />

        <meta key="og:url" property="og:url" content={canonicalURL} />
      </Head>
      <ColumnListTopPage />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;
Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
